/**
 * The half of the palette that acts on what is already written.
 *
 * Articulations, ornaments, dynamics and lines all work the same way: they need a selection,
 * so they are greyed out without one rather than failing with a sentence after the click.
 *
 * The Lines section holds one of each line. The slur below the notes and the three other
 * octave signs are in the Line menu, which is the whole vocabulary; six buttons is what fits
 * across the palette without becoming a list of near-identical squares.
 *
 * The ornaments here are the ones a hand reaches for without thinking -- a trill, the two
 * mordents, the two turns, a fermata, an arpeggio, a tremolo. The rarer signs, the fermata
 * shapes and the tremolo counts are in the Ornament menu, which is the whole vocabulary; a
 * palette that held all of it would be a menu with worse labels.
 */

import { marks } from "@app/score-editor";
import type { DynamicMark, Ornament } from "@app/score-editor";
import { Button, Section } from "./ScorePaletteBits";
import {
  ARPEGGIO_PATH,
  CRESCENDO_PATH,
  DIMINUENDO_PATH,
  FERMATA_PATH,
  GlissandoGlyph,
  Glyph,
  INVERTED_MORDENT_PATH,
  INVERTED_TURN_PATH,
  MORDENT_PATH,
  PEDAL_PATH,
  SLUR_PATH,
  TREMOLO_PATH,
  TURN_PATH,
} from "./ScoreGlyphs";
import { ARTICULATIONS } from "../lib/score-menus-music";
import type { ScoreActions } from "../lib/score-actions";

const DYNAMICS: readonly DynamicMark[] = ["pp", "p", "mp", "mf", "f", "ff"];

/** A mark drawn as the character an engraver would recognise it by. */
const ARTICULATION_SIGNS: Readonly<Record<string, string>> = {
  staccato: "•",
  staccatissimo: "▾",
  accent: ">",
  "strong-accent": "^",
  tenuto: "—",
  "detached-legato": "‿",
  "breath-mark": "’",
  harmonic: "○",
};

/** The ornaments the palette offers, and the path each is drawn with. */
const ORNAMENT_BUTTONS: readonly { mark: Ornament; label: string; path: string | null }[] = [
  { mark: "trill", label: "Trill", path: null },
  { mark: "mordent", label: "Mordent", path: MORDENT_PATH },
  { mark: "inverted-mordent", label: "Inverted mordent", path: INVERTED_MORDENT_PATH },
  { mark: "turn", label: "Turn", path: TURN_PATH },
  { mark: "inverted-turn", label: "Inverted turn", path: INVERTED_TURN_PATH },
];

export default function ScorePaletteMarks({ actions }: { actions: ScoreActions }): JSX.Element {
  const { score, selection } = actions;
  const nothing = selection.events.size === 0;

  return (
    <>
      <Section title="Articulation">
        {ARTICULATIONS.map((articulation) => (
          <Button
            key={articulation.mark}
            title={articulation.label}
            disabled={nothing}
            onClick={() => actions.edit(marks.articulate(score, selection, articulation.mark))}
          >
            <span className="text-[15px] leading-none">
              {ARTICULATION_SIGNS[articulation.mark] ?? "•"}
            </span>
          </Button>
        ))}
      </Section>

      <Section title="Ornament">
        {ORNAMENT_BUTTONS.map((ornament) => (
          <Button
            key={ornament.mark}
            title={ornament.label}
            disabled={nothing}
            onClick={() => actions.edit(marks.ornament(score, selection, ornament.mark))}
          >
            {ornament.path === null ? (
              <span className="font-serif text-[13px] font-bold italic">tr</span>
            ) : (
              <Glyph d={ornament.path} />
            )}
          </Button>
        ))}
        <Button
          title="Fermata"
          disabled={nothing}
          onClick={() => actions.edit(marks.toggleFermata(score, selection))}
        >
          <Glyph d={FERMATA_PATH} />
        </Button>
        <Button
          title="Arpeggio"
          disabled={nothing}
          onClick={() => actions.edit(marks.arpeggio(score, selection, "up"))}
        >
          <Glyph d={ARPEGGIO_PATH} />
        </Button>
        <Button
          title="Tremolo, three beams"
          disabled={nothing}
          onClick={() => actions.edit(marks.tremolo(score, selection, 3))}
        >
          <Glyph d={TREMOLO_PATH} />
        </Button>
        <Button
          title="No ornament"
          disabled={nothing}
          onClick={() => actions.edit(marks.clearOrnaments(score, selection))}
        >
          <span className="text-[13px] leading-none">✕</span>
        </Button>
      </Section>

      <Section title="Dynamics">
        {DYNAMICS.map((mark) => (
          <Button
            key={mark}
            title={`Dynamic ${mark}`}
            disabled={nothing}
            onClick={() => actions.edit(marks.dynamic(score, selection, mark))}
          >
            <span className="font-serif text-[13px] font-bold italic">{mark}</span>
          </Button>
        ))}
      </Section>

      <Section title="Lines">
        <Button
          title="Slur the selection"
          disabled={nothing}
          onClick={() => actions.edit(marks.slur(score, selection))}
        >
          <Glyph d={SLUR_PATH} />
        </Button>
        <Button
          title="Crescendo"
          disabled={nothing}
          onClick={() => actions.edit(marks.hairpin(score, selection, "crescendo"))}
        >
          <Glyph d={CRESCENDO_PATH} />
        </Button>
        <Button
          title="Diminuendo"
          disabled={nothing}
          onClick={() => actions.edit(marks.hairpin(score, selection, "diminuendo"))}
        >
          <Glyph d={DIMINUENDO_PATH} />
        </Button>
        <Button
          title="Glissando"
          disabled={nothing}
          onClick={() => actions.edit(marks.glissando(score, selection))}
        >
          <GlissandoGlyph />
        </Button>
        <Button
          title="Pedal"
          disabled={nothing}
          onClick={() => actions.edit(marks.pedal(score, selection))}
        >
          <Glyph d={PEDAL_PATH} />
        </Button>
        <Button
          title="Octave up, 8va"
          disabled={nothing}
          onClick={() => actions.edit(marks.octave(score, selection, "8va"))}
        >
          <span className="font-serif text-[13px] font-bold italic">8va</span>
        </Button>
      </Section>
    </>
  );
}
